/* =========================================================
   Affiche TIR À L'ARC — le blason, le score, la progression, l'arc,
   le palmarès.
   API : SNTaCible.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe, blason, distance,
              mode 'blason'      → v1..v6 (« X,10,9,9,8,8 ») : une volée par champ
              mode 'score'       → v1..v6, plus discipline
              mode 'progression' → r1..r12 (scores successifs)
              mode 'arc'         → l1..l6 (« Libellé|valeur »)
              mode 'palmares'    → p1..p8 (« 12 juin|Chartres|648|1er ») }
     opts : { palette }

   Le blason est aux cotes de World Archery : dix zones concentriques, le
   diamètre de la zone N valant D × (11−N)/10, et les couleurs officielles par
   paires. Le choix du blason passe par une TABLE FERMÉE — la valeur finit dans
   des calculs de géométrie et jamais dans un attribut, mais une saisie libre
   qui atteint un `fill` est exactement le trou par lequel un SVG se casse.

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    nuit:  { label:'Nuit',  bg:'#0E1B2A', ink:'#F4F1E8', muted:'#8B9BAE', accent:'#E8B33D', line:'#22364C', card:'#17263A' },
    craie: { label:'Craie', bg:'#F7F4ED', ink:'#12202E', muted:'#7C8794', accent:'#C2410C', line:'#E3DDD0', card:'#FFFFFF' },
    foret: { label:'Forêt', bg:'#16281F', ink:'#F1F0E6', muted:'#8FA894', accent:'#E8B33D', line:'#2A4034', card:'#1E3428' },
    ocre:  { label:'Ocre',  bg:'#2A2015', ink:'#F5EFE3', muted:'#B09B7C', accent:'#E8B33D', line:'#463526', card:'#382A1C' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  /* Blasons homologués. TABLE FERMÉE : la saisie du client sert de CLÉ, jamais
     de valeur — un identifiant inconnu retombe sur le 122. */
  const BLASONS = {
    '122': { cm: 122, distance: '70 m' },
    '80':  { cm: 80,  distance: '50 m' },
    '60':  { cm: 60,  distance: '25 m' },
    '40':  { cm: 40,  distance: '18 m' },
  };
  /* Couleurs World Archery, par paires de zones, du centre vers l'extérieur.
     Le trait de séparation est noir sur les zones claires et blanc sur le noir,
     comme sur un vrai blason. */
  const ZONES = [
    { n: 10, fond: '#FFD800', trait: '#1D1D1B' }, { n: 9,  fond: '#FFD800', trait: '#1D1D1B' },
    { n: 8,  fond: '#F1443A', trait: '#1D1D1B' }, { n: 7,  fond: '#F1443A', trait: '#1D1D1B' },
    { n: 6,  fond: '#3FBCE8', trait: '#1D1D1B' }, { n: 5,  fond: '#3FBCE8', trait: '#1D1D1B' },
    { n: 4,  fond: '#1D1D1B', trait: '#FFFFFF' }, { n: 3,  fond: '#1D1D1B', trait: '#FFFFFF' },
    { n: 2,  fond: '#FFFFFF', trait: '#1D1D1B' }, { n: 1,  fond: '#FFFFFF', trait: '#1D1D1B' },
  ];

  /** Une volée « X,10,9,9,8,8 » → [{v:10,x:true}, {v:10}, …]. M ou 0 = manqué. */
  function fleches(raw) {
    return String(raw == null ? '' : raw).split(/[,;\s]+/).map(s => s.trim()).filter(Boolean).map(s => {
      const u = s.toUpperCase();
      if (u === 'X') return { v: 10, x: true, z: 10 };
      if (u === 'M' || u === '0') return { v: 0, z: 0 };
      const n = parseInt(u, 10);
      return Number.isFinite(n) && n >= 1 && n <= 10 ? { v: n, z: n } : null;
    }).filter(Boolean);
  }
  function toutesLesFleches(data) {
    const l = [];
    for (let i = 1; i <= 6; i++) for (const f of fleches(data['v' + i])) l.push(f);
    return l;
  }

  /* ---------------------------------------------------------------
     LE BLASON. Zone N : diamètre D × (11−N)/10. La zone 10 fait donc un
     dixième du blason, et le X — le 10 intérieur — la moitié de celle-ci.
     --------------------------------------------------------------- */
  function blason(cx, cy, R, tirs, p) {
    let g = '';
    /* DU PLUS GRAND AU PLUS PETIT. Des disques pleins concentriques ne se
       superposent correctement que dans cet ordre : dessinée après, la zone 1
       — un disque blanc de tout le blason — recouvrirait les neuf autres. */
    for (let i = ZONES.length - 1; i >= 0; i--) {
      const z = ZONES[i], r = R * (11 - z.n) / 10;
      g += `<circle cx="${cx}" cy="${cy}" r="${r.toFixed(1)}" fill="${z.fond}" stroke="${z.trait}" stroke-width="${Math.max(0.8, R * 0.006).toFixed(1)}"/>`;
    }
    /* le X, cercle intérieur du 10 : un simple trait, pas une zone colorée */
    g += `<circle cx="${cx}" cy="${cy}" r="${(R * 0.05).toFixed(1)}" fill="none" stroke="#1D1D1B" stroke-width="${Math.max(0.8, R * 0.005).toFixed(1)}"/>`;

    /* IMPACTS. L'angle suit l'angle d'or et le rayon oscille dans l'épaisseur
       de sa zone : deux flèches de même valeur ne se superposent donc jamais,
       et la même saisie redonne toujours la même affiche — condition absolue
       pour que le fichier d'impression corresponde à l'aperçu. */
    tirs.forEach((t, i) => {
      if (!t.z) return;                                   /* un manqué ne se dessine pas */
      const rExt = R * (11 - t.z) / 10, rInt = R * (10 - t.z) / 10;
      const bande = t.x ? [0, R * 0.05] : [rInt, rExt];
      const k = ((i * 0.6180339887) % 1) * 0.62 + 0.19;    /* dans l'épaisseur, jamais sur un trait */
      const rr = bande[0] + (bande[1] - bande[0]) * k;
      const a = (i * 137.508 + 18) * Math.PI / 180;
      g += `<circle cx="${(cx + rr * Math.cos(a)).toFixed(1)}" cy="${(cy + rr * Math.sin(a)).toFixed(1)}"`
        + ` r="${Math.max(3.4, R * 0.026).toFixed(1)}" fill="#FFFFFF" stroke="#1D1D1B" stroke-width="1.6"/>`;
    });
    return g;
  }

  function stats(p, items, y) {
    const it = (items || []).filter(x => x && String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Filigrane : les anneaux du blason, en cercles ouverts. */
  function filigrane(p) {
    let g = '';
    for (let i = 1; i <= 5; i++)
      g += `<circle cx="400" cy="566" r="${i * 43}" fill="none" stroke="${p.ink}" stroke-width="15"/>`;
    return `<g opacity="0.055">${g}</g>`;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.nuit;
    const mode = String(data.mode || 'blason').toLowerCase();
    const cx = PW / 2;
    const B = BLASONS[String(data.blason || '').replace(/[^0-9]/g, '')] || BLASONS['122'];

    const DEF = { blason: 'LE BLASON', score: 'MON SCORE', progression: 'MA PROGRESSION', arc: 'MON ARC', palmares: 'MON PALMARÈS' };
    const titre = up(data.titre) || DEF[mode] || 'LE BLASON';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || 'TIR À L’ARC';
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'blason') {
      const tirs = toutesLesFleches(data);
      corps = blason(cx, 578, 250, tirs, p)
        + T(cx, 862, 11.5, p.muted, `BLASON ${B.cm} CM · ${esc(up(data.distance)) || B.distance}`, { w:600, ls:2.6 });
      if (tirs.length) {
        const total = tirs.reduce((a, t) => a + t.v, 0);
        const dix = tirs.filter(t => t.z === 10).length, x = tirs.filter(t => t.x).length;
        barre = stats(p, [[`${total}`, `sur ${tirs.length * 10}`], [`${tirs.length}`, 'Flèches'], [`${dix}`, x ? `Dix, dont ${x} X` : 'Dix']], 962);
      } else {
        barre = stats(p, [[String(B.cm) + ' cm', 'Blason'], [B.distance, 'Distance'], ['10', 'Zones']], 962);
      }
      pied = 1030;

    } else if (mode === 'score') {
      const volees = [];
      for (let i = 1; i <= 6; i++) {
        const f = fleches(data['v' + i]);
        if (f.length) volees.push(f);
      }
      const tirs = volees.flat();
      const total = tirs.reduce((a, t) => a + t.v, 0);
      corps = `<rect x="${MX}" y="268" width="${PW-2*MX}" height="128" rx="14" fill="${p.ink}"/>`
        + T(cx, 308, 13, p.accent, esc(up(data.discipline)) || `BLASON ${B.cm} CM · ${B.distance}`, { w:700, ls:6 })
        + `<text x="${cx}" y="368" text-anchor="middle" font-family="Oswald" font-weight="700" fill="${p.bg}"><tspan font-size="58">${tirs.length ? total : '—'}</tspan><tspan font-size="26" fill="${p.muted}" dx="10">/ ${tirs.length * 10 || '—'}</tspan></text>`;
      if (volees.length) {
        const zone = 480, pas = Math.min(88, zone / volees.length), y0 = 470 + (zone - pas * volees.length) / 2;
        volees.forEach((f, i) => {
          const y = y0 + i * pas, s = f.reduce((a, t) => a + t.v, 0);
          corps += T(MX, y + 4, 13, p.muted, 'VOLÉE ' + (i + 1), { w:600, ls:2.5, a:'start' })
            + T(cx + 40, y + 6, fit(f.map(t => t.x ? 'X' : (t.v || 'M')).join('  '), 340, 24, 0.62, 0, 13), p.ink,
                esc(f.map(t => t.x ? 'X' : (t.v || 'M')).join('  ')), { f:'Oswald', w:700 })
            + T(PW - MX, y + 8, 30, p.accent, String(s), { f:'Oswald', w:700, a:'end' });
          if (i < volees.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 6).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 6).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps += T(cx, 620, 20, p.muted, 'UNE VOLÉE PAR LIGNE : X,10,9,9,8,8', { w:600, ls:2 });
      pied = 1010;

    } else if (mode === 'progression') {
      const v = [];
      for (let i = 1; i <= 12; i++) { const n = num(data['r' + i]); if (n != null) v.push(n); }
      if (v.length >= 2) {
        const X0 = MX + 30, X1 = PW - MX - 10, Y0 = 340, Y1 = 800;
        const lo = Math.min.apply(null, v), hi = Math.max.apply(null, v);
        const etendue = hi - lo || 1, marge = etendue * 0.18;
        const px = i => X0 + (X1 - X0) * i / (v.length - 1);
        const py = n => Y1 - (Y1 - Y0) * (n - (lo - marge)) / (etendue + 2 * marge);
        for (let k = 0; k <= 3; k++) {
          const yy = Y0 + (Y1 - Y0) * k / 3;
          corps += `<line x1="${X0}" y1="${yy.toFixed(1)}" x2="${X1}" y2="${yy.toFixed(1)}" stroke="${p.line}" stroke-width="1.2"/>`;
        }
        corps += `<polyline points="${v.map((n, i) => `${px(i).toFixed(1)},${py(n).toFixed(1)}`).join(' ')}" fill="none" stroke="${p.accent}" stroke-width="3.5" stroke-linejoin="round" stroke-linecap="round"/>`;
        v.forEach((n, i) => {
          corps += `<circle cx="${px(i).toFixed(1)}" cy="${py(n).toFixed(1)}" r="${i === v.length - 1 ? 8 : 5}" fill="${i === v.length - 1 ? p.accent : p.bg}" stroke="${p.accent}" stroke-width="2.5"/>`;
        });
        corps += T(px(0), py(v[0]) - 20, 15, p.muted, String(v[0]), { f:'Oswald', w:700 })
          + T(px(v.length - 1), py(v[v.length - 1]) - 22, 22, p.ink, String(v[v.length - 1]), { f:'Oswald', w:700 });
        barre = stats(p, [[String(v[v.length - 1]), 'Dernier score'], [String(hi), 'Record'],
          [(v[v.length - 1] - v[0] >= 0 ? '+' : '') + (v[v.length - 1] - v[0]), 'Depuis le début']], 962);
      } else corps = T(cx, 600, 20, p.muted, 'DEUX SCORES SUFFISENT POUR TRACER LA COURBE', { w:600, ls:2 });
      pied = 1030;

    } else if (mode === 'arc') {
      /* L'ARC, de profil et bandé. Deux branches en miroir, la corde tendue
         entre les poupées, la poignée entre les deux. Aucune donnée du client
         n'y est représentée : c'est le portrait de l'objet, les cotes sont
         dans les lignes du bas. */
      const ax = cx - 150, ay = 470, h = 190;
      /* La poupée part LÉGÈREMENT DERRIÈRE la corde avant de repartir vers
         l'avant : c'est ce crochet qui distingue un arc classique d'un arc
         droit, et sur une silhouette c'est le seul détail qui le dise. */
      const branche = s => `M ${ax} ${(ay + s*h).toFixed(1)}`
        + ` C ${(ax - 9)} ${(ay + s*h*0.92).toFixed(1)} ${(ax + 78)} ${(ay + s*h*0.78).toFixed(1)} ${(ax + 88)} ${(ay + s*h*0.24).toFixed(1)}`;
      corps = `<path d="${branche(-1)}" fill="none" stroke="${p.ink}" stroke-width="9" stroke-linecap="round"/>`
        + `<path d="${branche(1)}" fill="none" stroke="${p.ink}" stroke-width="9" stroke-linecap="round"/>`
        + `<rect x="${ax + 80}" y="${(ay - h*0.26).toFixed(1)}" width="17" height="${(h*0.52).toFixed(1)}" rx="8" fill="${p.ink}"/>`
        + `<line x1="${ax}" y1="${(ay - h).toFixed(1)}" x2="${ax}" y2="${(ay + h).toFixed(1)}" stroke="${p.muted}" stroke-width="2.5"/>`
        /* la flèche encochée, et le repose-flèche */
        + `<line x1="${ax - 6}" y1="${ay}" x2="${(ax + 300)}" y2="${ay}" stroke="${p.accent}" stroke-width="4" stroke-linecap="round"/>`
        + `<path d="M ${(ax + 300)} ${ay} l -16 -9 l 0 18 z" fill="${p.accent}"/>`;
      const l = [];
      for (let i = 1; i <= 6; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, val] = raw.split('|').map(s => (s || '').trim());
        if (lab && val) l.push([lab, val]);
      }
      if (l.length) {
        const y0 = 726, pas = Math.min(58, 300 / l.length);
        l.forEach((r, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y, 14, p.muted, esc(up(r[0])), { w:600, ls:2.5, a:'start' })
            + T(PW - MX, y + 4, fit(r[1], 360, 26, 0.58, 0, 14), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 6).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 6).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps += T(cx, 760, 20, p.muted, 'FORMAT : LE LIBELLÉ, UNE BARRE VERTICALE, LA VALEUR', { w:600, ls:1.5 });
      pied = 1030;

    } else { /* palmares */
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['p' + i] || '').trim(); if (!raw) continue;
        const c = raw.split('|').map(s => (s || '').trim());
        if (c[1]) l.push(c);
      }
      if (l.length) {
        const zone = 620, pas = Math.min(102, zone / l.length), y0 = 340 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, podium = /^(1er|2e|3e|1|2|3)\b/i.test(r[3] || '');
          corps += (r[0] ? T(MX, y - 12, 13, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' }) : '')
            + T(MX, y + 16, fit(r[1], 400, 26, 0.60, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'start' })
            + (r[2] ? T(PW - MX - 74, y + 16, 30, p.ink, esc(r[2]), { f:'Oswald', w:700, a:'end' }) : '')
            + (r[3] ? T(PW - MX, y + 14, 20, podium ? p.accent : p.muted, esc(up(r[3])), { f:'Oswald', w:700, a:'end' }) : '');
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 7).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 7).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : DATE, CONCOURS, SCORE, PLACE', { w:600, ls:2 });
      pied = 1000;
    }

    /* Le filigrane n'est posé que sur les modes où la place est vraiment
       vide : là où un dessin occupe déjà l'affiche, il ne ferait que salir. */
    const fili = ["score","progression","palmares"].indexOf(mode) >= 0 ? filigrane(p) : '';
    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${fili}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNTaCible = { render, PALETTES, BLASONS };
})();
